import {Button, type ButtonProps} from '@famly/mf_input_button';
import i18next from 'i18next';
import {useBreakpoints} from '@famly/mf_util_breakpoints';
import React from 'react';

type ModalPrimaryButtonSubmitProps = {
    type?: Extract<ButtonProps['type'], 'submit'>;
    onClick?: undefined;
};

type ModalPrimaryButtonButtonProps = {
    type?: Extract<ButtonProps['type'], 'button'>;
    onClick?: ButtonProps['onClick'];
};

type ModalPrimaryButtonProps = {
    text?: ButtonProps['text'];
    icon?: ButtonProps['icon'];
    isDisabled?: ButtonProps['isDisabled'];
} & (ModalPrimaryButtonButtonProps | ModalPrimaryButtonSubmitProps);

/**
 * ModalPrimaryButton component
 *
 * The primary action button for modals, typically used for confirmations or saving.
 * Renders as a primary button with responsive sizing based on viewport width.
 *
 * - On mobile: Regular size
 * - On tablet and larger: Compact size
 *
 * Should be used inside ModalFooterButtons.
 *
 * @param {ModalPrimaryButtonProps} props - Component props
 * @param {ButtonProps['type']} [props.type='submit'] - Button type attribute
 * @param {ButtonProps['text']} [props.text='Confirm'] - Button text
 * @param {ButtonProps['icon']} [props.icon] - Optional icon to display in the button
 * @param {ButtonProps['isDisabled']} [props.isDisabled] - Optional isDisabled in the button
 *
 * @example
 * <ModalFooterButtons>
 *   <ModalPrimaryButton text="Save" onClick={handleSave} />
 * </ModalFooterButtons>
 */
export const ModalPrimaryButton = ({
    type = 'submit',
    text = i18next.t('mf.modalsDialogs_modal.src.translations.json.confirmButtonLabel'),
    icon,
    isDisabled,
    onClick,
}: ModalPrimaryButtonProps) => {
    const {isTabletPortraitAndLarger} = useBreakpoints();

    return (
        <Button
            fullWidth
            size={isTabletPortraitAndLarger ? 'compact' : 'regular'}
            type={type}
            variant="primary"
            text={text}
            icon={icon}
            isDisabled={isDisabled}
            order={isTabletPortraitAndLarger ? 3 : 1}
            onClick={onClick}
        />
    );
};
